/**
 * Application Constants
 * Centralized constants for the entire application
 */

// HTTP Status Codes
export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  UNPROCESSABLE_ENTITY: 422,
  INTERNAL_SERVER_ERROR: 500,
  BAD_GATEWAY: 502,
  SERVICE_UNAVAILABLE: 503,
};

// API Endpoints
export const API_ENDPOINTS = {
  AUTH: {
    LOGIN: 'auth/login/',
    REGISTER: 'register/',
    LOGOUT: 'token/logout/',
    REFRESH: 'token/refresh/',
    FORGOT_PASSWORD: 'auth/forgot-password/',
    RESET_PASSWORD: 'auth/reset-password/',
    VERIFY_2FA: 'auth/2fa/verify/',
    RESEND_2FA: 'auth/2fa/resend/',
  },
  USER: {
    PROFILE: 'user/profile',
    UPDATE_PROFILE: 'user/profile',
    REQUEST_EMAIL_CHANGE: 'user/change-email/request/',
    CONFIRM_EMAIL_CHANGE: 'user/change-email/confirm/',
    CHANGE_PASSWORD: 'user/change-password/',
  },
  CONTACT: {
    ROOT: 'contact-submissions/',
  },
  FALL_EVENTS: {
    ROOT: 'fall-events/',
    OPEN_COUNT: 'fall-events/open-count/',
  },
  ADMIN: {
    DASHBOARD: 'admin-dashboard/',
  },
  MONITORING: {
    START: 'start_live_detection/',
    STOP: 'stop_live_detection/',
    FEED: (cameraId) => `live-video-feed/${cameraId}/`,
    HEARTBEAT: 'camera-motion/heartbeat/',
  },
  // Add more endpoint categories as needed
};

// Local Storage Keys
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'auth_token',
  REFRESH_TOKEN: 'refresh_token',
  USER_DATA: 'user_data',
  THEME: 'app_theme',
  LANGUAGE: 'app_language',
  PREFERENCES: 'user_preferences',
};

// Route Names
export const ROUTES = {
  HOME: '/',
  LOGIN: '/login',
  REGISTER: '/register',
  DASHBOARD: '/dashboard',
  PROFILE: '/profile',
  SETTINGS: '/settings',
  NOT_FOUND: '/404',
};

// Form Validation Messages
export const VALIDATION_MESSAGES = {
  REQUIRED: 'This field is required',
  EMAIL: 'Please enter a valid email address',
  PASSWORD_MIN_LENGTH: 'Password must be at least 8 characters long',
  PASSWORD_MATCH: 'Passwords must match',
  INVALID_FORMAT: 'Invalid format',
};

// Error Messages
export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Network error. Please check your connection.',
  UNAUTHORIZED: 'You are not authorized to perform this action.',
  FORBIDDEN: 'Access denied.',
  NOT_FOUND: 'The requested resource was not found.',
  SERVER_ERROR: 'An internal server error occurred.',
  UNKNOWN_ERROR: 'An unknown error occurred.',
  BAD_REQUEST: 'Invalid request. Please check your input.',
  CONFLICT: 'A conflict occurred with the current state of the resource.',
  UNPROCESSABLE_ENTITY: 'The request was well-formed but contains semantic errors.',
  LOGIN_FAILED: 'Login failed. Please check your credentials.',
  REGISTER_FAILED: 'Registration failed. Please try again.',
};

// Success Messages
export const SUCCESS_MESSAGES = {
  LOGIN_SUCCESS: 'Login successful!',
  REGISTER_SUCCESS: 'Registration successful!',
  PROFILE_UPDATED: 'Profile updated successfully!',
  PASSWORD_CHANGED: 'Password changed successfully!',
  DATA_SAVED: 'Data saved successfully!',
};

// Theme Types
export const THEME_TYPES = {
  LIGHT: 'light',
  DARK: 'dark',
  SYSTEM: 'system',
};

// Language Codes
export const LANGUAGES = {
  EN: 'en',
  ES: 'es',
  FR: 'fr',
  DE: 'de',
  // Add more languages as needed
};

// User Roles
export const USER_ROLES = {
  ADMIN: 'admin',
  USER: 'user',
  MODERATOR: 'moderator',
  GUEST: 'guest',
};

// Pagination
export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 10,
  PAGE_SIZE_OPTIONS: [5, 10, 20, 50, 100],
};

// File Upload
export const FILE_UPLOAD = {
  MAX_SIZE: 5 * 1024 * 1024, // 5MB
  ALLOWED_TYPES: ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'],
  MAX_FILES: 5,
};

// Date Formats
export const DATE_FORMATS = {
  DISPLAY: 'MMM dd, yyyy',
  API: 'yyyy-MM-dd',
  DATETIME: 'MMM dd, yyyy HH:mm',
  TIME: 'HH:mm',
};

// Animation Durations
export const ANIMATION_DURATIONS = {
  FAST: 150,
  NORMAL: 300,
  SLOW: 500,
};

// Breakpoints (in pixels)
export const BREAKPOINTS = {
  MOBILE: 576,
  TABLET: 768,
  DESKTOP: 992,
  LARGE: 1200,
  EXTRA_LARGE: 1400,
}; 
